export interface MbiraKeyDef {
  id: string; // Note name (e.g., "G3", "B4")
  freq: number;
  keys: string[];
  manual: "left" | "right" | "upper";
  index: number; // Position within the manual
}

// Helper to create a key definition
const k = (
  note: string,
  freq: number,
  keys: string[],
  manual: "left" | "right" | "upper",
  index: number
): MbiraKeyDef => ({ id: note, freq, keys, manual, index });

// Note Frequencies
const NOTES: Record<string, number> = {
  // Octave 2
  G2: 98.00, A2: 110.00, B2: 123.47, C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61,
  // Octave 3
  G3: 196.00, A3: 220.00, Bb3: 233.08, B3: 246.94, C4: 261.63, D4: 293.66, Eb4: 311.13, E4: 329.63, F4: 349.23,
  // Octave 4
  G4: 392.00, A4: 440.00, Bb4: 466.16, B4: 493.88, C5: 523.25, D5: 587.33, Eb5: 622.25, E5: 659.25, F5: 698.46,
  // Octave 5
  G5: 783.99, A5: 880.00, Bb5: 932.33, B5: 987.77, C6: 1046.50, D6: 1174.66, E6: 1318.51,
};

// Mbira dzavadzimu layout:
// - Left manual (L): 7 keys, bass, played with left thumb - keys go outward (index 0 = innermost)
// - Right manual (R): 8 keys, middle, played with right thumb - keys go outward
// - Upper manual (U): 6 keys, high, played with right index finger

// Keyboard mapping:
// Left manual:  U Y T R E W Q (inner to outer, bass notes)
// Right manual: H J K L ; ' ] (inner to outer, mid notes)
// Upper manual: 6 5 4 3 2 1 (inner to outer, high notes)

// Traditional Nyamaropa tuning (G-based, approximated to Western scale)
// This is the most common mbira tuning, used for healing ceremonies
const NYAMAROPA_KEYS: MbiraKeyDef[] = [
  // Left manual - bass register (innermost to outermost)
  k("G3", NOTES.G3, ["u"], "left", 0),
  k("E3", NOTES.E3, ["y"], "left", 1),
  k("D3", NOTES.D3, ["t"], "left", 2),
  k("C3", NOTES.C3, ["r"], "left", 3),
  k("B2", NOTES.B2, ["e"], "left", 4),
  k("A2", NOTES.A2, ["w"], "left", 5),
  k("G2", NOTES.G2, ["q"], "left", 6),

  // Right manual - middle register (innermost to outermost)
  k("A3", NOTES.A3, ["h"], "right", 0),
  k("B3", NOTES.B3, ["j"], "right", 1),
  k("C4", NOTES.C4, ["k"], "right", 2),
  k("D4", NOTES.D4, ["l"], "right", 3),
  k("E4", NOTES.E4, [";"], "right", 4),
  k("G4", NOTES.G4, ["'"], "right", 5),
  k("A4", NOTES.A4, ["["], "right", 6),
  k("B4", NOTES.B4, ["]"], "right", 7),

  // Upper manual - high register (innermost to outermost)
  k("C5", NOTES.C5, ["6"], "upper", 0),
  k("D5", NOTES.D5, ["5"], "upper", 1),
  k("E5", NOTES.E5, ["4"], "upper", 2),
  k("G5", NOTES.G5, ["3"], "upper", 3),
  k("A5", NOTES.A5, ["2"], "upper", 4),
  k("B5", NOTES.B5, ["1"], "upper", 5),
];

// Gandanga tuning - more energetic, for dancing
const GANDANGA_KEYS: MbiraKeyDef[] = [
  // Left manual
  k("G3", NOTES.G3, ["u"], "left", 0),
  k("F3", NOTES.F3, ["y"], "left", 1),
  k("E3", NOTES.E3, ["t"], "left", 2),
  k("D3", NOTES.D3, ["r"], "left", 3),
  k("C3", NOTES.C3, ["e"], "left", 4),
  k("B2", NOTES.B2, ["w"], "left", 5),
  k("A2", NOTES.A2, ["q"], "left", 6),

  // Right manual
  k("A3", NOTES.A3, ["h"], "right", 0),
  k("B3", NOTES.B3, ["j"], "right", 1),
  k("C4", NOTES.C4, ["k"], "right", 2),
  k("D4", NOTES.D4, ["l"], "right", 3),
  k("E4", NOTES.E4, [";"], "right", 4),
  k("F4", NOTES.F4, ["'"], "right", 5),
  k("G4", NOTES.G4, ["["], "right", 6),
  k("A4", NOTES.A4, ["]"], "right", 7),

  // Upper manual
  k("B4", NOTES.B4, ["6"], "upper", 0),
  k("C5", NOTES.C5, ["5"], "upper", 1),
  k("D5", NOTES.D5, ["4"], "upper", 2),
  k("E5", NOTES.E5, ["3"], "upper", 3),
  k("F5", NOTES.F5, ["2"], "upper", 4),
  k("G5", NOTES.G5, ["1"], "upper", 5),
];

// Mavembe tuning - minor feel, introspective
const MAVEMBE_KEYS: MbiraKeyDef[] = [
  // Left manual
  k("G3", NOTES.G3, ["u"], "left", 0),
  k("Eb3", 155.56, ["y"], "left", 1),
  k("D3", NOTES.D3, ["t"], "left", 2),
  k("C3", NOTES.C3, ["r"], "left", 3),
  k("Bb2", 116.54, ["e"], "left", 4),
  k("A2", NOTES.A2, ["w"], "left", 5),
  k("G2", NOTES.G2, ["q"], "left", 6),

  // Right manual
  k("A3", NOTES.A3, ["h"], "right", 0),
  k("Bb3", NOTES.Bb3, ["j"], "right", 1),
  k("C4", NOTES.C4, ["k"], "right", 2),
  k("D4", NOTES.D4, ["l"], "right", 3),
  k("Eb4", NOTES.Eb4, [";"], "right", 4),
  k("G4", NOTES.G4, ["'"], "right", 5),
  k("A4", NOTES.A4, ["["], "right", 6),
  k("Bb4", NOTES.Bb4, ["]"], "right", 7),

  // Upper manual
  k("C5", NOTES.C5, ["6"], "upper", 0),
  k("D5", NOTES.D5, ["5"], "upper", 1),
  k("Eb5", NOTES.Eb5, ["4"], "upper", 2),
  k("G5", NOTES.G5, ["3"], "upper", 3),
  k("A5", NOTES.A5, ["2"], "upper", 4),
  k("Bb5", NOTES.Bb5, ["1"], "upper", 5),
];

export const TUNINGS = {
  nyamaropa: {
    name: "Nyamaropa (Traditional)",
    keys: NYAMAROPA_KEYS,
  },
  gandanga: {
    name: "Gandanga (Dance)",
    keys: GANDANGA_KEYS,
  },
  mavembe: {
    name: "Mavembe (Minor)",
    keys: MAVEMBE_KEYS,
  },
} as const;

export type TuningType = keyof typeof TUNINGS;
